import React, { useState } from 'react';
import { Card } from '../../Shared/ui/card';
import { Button } from '../../Shared/ui/button';
import apiClient from '../../Shared/services/api';
import type { SubscriptionInfo, BillingActions } from '../../Shared/types';

interface BillingStatusCardProps {
  subscription: SubscriptionInfo;
  billingActions: BillingActions;
  onRefresh: () => Promise<void>;
}

const formatDate = (isoDate: string): string => {
  return new Date(isoDate).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

const BillingStatusCard: React.FC<BillingStatusCardProps> = ({
  subscription,
  billingActions,
  onRefresh,
}) => {
  const [cancelLoading, setCancelLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const handleCancel = async (): Promise<void> => {
    setCancelLoading(true);
    try {
      await apiClient.cancelSubscription();
      await onRefresh();
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleResume = async (): Promise<void> => {
    setResumeLoading(true);
    try {
      await apiClient.resumeSubscription();
      await onRefresh();
    } catch (err) {
      console.error('Failed to resume subscription:', err);
    } finally {
      setResumeLoading(false);
    }
  };

  const handleManageBilling = async (): Promise<void> => {
    setPortalLoading(true);
    try {
      const { portal_url } = await apiClient.getBillingPortalUrl();
      window.location.href = portal_url;
    } catch (err) {
      console.error('Failed to open billing portal:', err);
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">Subscription</h2>

      <div className="space-y-3 mb-6">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Status</span>
          <span className="text-sm font-medium text-gray-900">{subscription.status_label}</span>
        </div>

        {subscription.current_period_ends_at && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Current period ends</span>
            <span className="text-sm text-gray-900">
              {formatDate(subscription.current_period_ends_at)}
            </span>
          </div>
        )}

        {subscription.trial_ends_at && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Trial ends</span>
            <span className="text-sm text-gray-900">{formatDate(subscription.trial_ends_at)}</span>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {billingActions.can_cancel && (
          <Button
            variant="outline"
            className="w-full"
            onClick={() => void handleCancel()}
            disabled={cancelLoading}
          >
            {cancelLoading ? 'Cancelling...' : 'Cancel Subscription'}
          </Button>
        )}

        {billingActions.can_resume && (
          <Button
            variant="outline"
            className="w-full"
            onClick={() => void handleResume()}
            disabled={resumeLoading}
          >
            {resumeLoading ? 'Resuming...' : 'Resume Subscription'}
          </Button>
        )}

        <Button
          className="w-full"
          onClick={() => void handleManageBilling()}
          disabled={portalLoading}
        >
          {portalLoading ? 'Loading...' : 'Manage Billing'}
        </Button>
      </div>
    </Card>
  );
};

export default BillingStatusCard;
